/**
 * 🍳 Dave's Kitchen — Chits-powered cooking + shop.
 *
 * 4 tabs:
 *  • 🍳 Cook   — pick recipe → 100% success → goes to The Table
 *  • 🍽️ Table  — 24h holding bay → auto-list to marketplace
 *  • 🛒 Shop   — Chits-only seeds + foods + 🧈 Knob of Butter gambit
 *  • 📜 Recipes — owned + locked catalog
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import ReceiptScanner from "@/components/ReceiptScanner";
import LedgerSnapshotCard from "@/components/LedgerSnapshotCard";
import DailyLogPanel from "@/components/DailyLogPanel";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(secs) {
  if (!secs || secs <= 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT(x => x + 1), 1000); return () => clearInterval(id); }, []);
  return t;
}

/* ─── Sound FX ─── */
let _audioCtx = null;
function ctx() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) { try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; } }
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
}
function playSizzle() {
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  // White-noise sizzle for 0.6s
  const bufSize = c.sampleRate * 0.6;
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
  const src = c.createBufferSource();
  src.buffer = buf;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.25, now + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
  src.connect(gain).connect(c.destination);
  src.start(now);
}
function playDing() {
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(1320, now);
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  osc.connect(gain).connect(c.destination);
  osc.start(now); osc.stop(now + 0.55);
}

export default function KitchenPage() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [badges, setBadges] = useState(null);
  const [tab, setTab] = useState("cook");
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [ledgerBump,  setLedgerBump]  = useState(0);
  const tick = useTick();

  const refresh = useCallback(async () => {
    try {
      const [s, r, b] = await Promise.all([
        axios.get(`${API}/kitchen/state`),
        axios.get(`${API}/kitchen/recipes`),
        axios.get(`${API}/kitchen/badges`),
      ]);
      setState(s.data); setRecipes(r.data?.recipes || []); setBadges(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dave's stove is offline.");
    }
  }, []);

  useEffect(() => { if (authLoading || !user) return; refresh(); }, [authLoading, user, refresh]);

  if (authLoading) return null;
  if (!user) return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header /><div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">🔒 Sign in to enter Dave's Kitchen.</div><Footer />
    </div>
  );
  if (!state) return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header /><div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Dave is warming up the pans…</div><Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="kitchen-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0A] to-[#1a0a05] p-5 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-3xl font-black tracking-wide text-amber-300" style={{ fontFamily: 'Anton, sans-serif' }}>
                🍳 DAVE'S KITCHEN
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                Cook Farm ingredients into healing food · 100% success · 30% recipe drop · 🧈 buy a knob, win an egg
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setScannerOpen(true)}
                className="px-3 py-1.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-sm text-amber-200 flex items-center gap-1.5 transition"
                title="Scan a grocery receipt into Cassandra's Ledger"
                data-testid="kt-open-receipt-scanner"
              >
                📷 <span className="font-bold tracking-wide">Scan Receipt</span>
              </button>
              <Pill icon="🪙" label="Chits" value={state.chits} testid="kt-wallet-chits" />
              <Pill icon="Ⓞ"  label="OD"    value={state.omni_dollars || 0} testid="kt-wallet-od" />
              <Pill icon="💋" label="Kisses" value={state.chefs_kiss_owned || 0} testid="kt-wallet-kiss" />
              <Pill icon="📜" label="Recipes" value={state.kitchen_recipes_owned?.length || 0} testid="kt-recipes-count" />
              <Pill icon="🍽️" label="On Table" value={state.holding_bay?.length || 0} testid="kt-bay-count" />
            </div>
          </div>
        </div>

        <LedgerSnapshotCard refreshKey={ledgerBump} />

        <div className="flex gap-2 mb-5 border-b border-neutral-800 overflow-x-auto">
          <TabBtn label="🍳 Cook"     active={tab === "cook"}   onClick={() => setTab("cook")}   testid="kt-tab-cook" />
          <TabBtn label={`🍽️ Table${(state.holding_bay?.length || 0) ? ` (${state.holding_bay.length})` : ""}`}
                  active={tab === "table"}  onClick={() => setTab("table")}  testid="kt-tab-table" />
          <TabBtn label="🛒 Shop"     active={tab === "shop"}    onClick={() => setTab("shop")}    testid="kt-tab-shop" />
          <TabBtn label="📜 Recipes"  active={tab === "recipes"} onClick={() => setTab("recipes")} testid="kt-tab-recipes" />
          <TabBtn label={`🏅 Badges${badges?.earned_count != null ? ` (${badges.earned_count}/${badges.total_count})` : ""}`}
                  active={tab === "badges"}  onClick={() => setTab("badges")}  testid="kt-tab-badges" />
        </div>

        {tab === "cook"    && <CookTab    state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "table"   && <TableTab   state={state} tick={tick} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "shop"    && <ShopTab    state={state} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {tab === "recipes" && <RecipesTab recipes={recipes} />}
        {tab === "badges"  && <BadgesTab  badges={badges} busy={busy} setBusy={setBusy} refresh={refresh} />}

        {/* 📊 Unified Daily Log — same log seen in Gym, Tracker, Calendar, AI Coach */}
        <div className="mt-8" data-testid="kitchen-daily-log">
          <DailyLogPanel title="Today's Meals + Workouts" />
        </div>
      </div>
      <ReceiptScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        capturedFrom="kitchen"
        onSaved={() => setLedgerBump((n) => n + 1)}
      />
      <Footer />
    </div>
  );
}

function Pill({ icon, label, value, testid }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5" data-testid={testid}>
      <span>{icon}</span>
      <span className="font-bold text-amber-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}
function TabBtn({ label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 text-sm font-bold border-b-2 transition whitespace-nowrap ${
        active ? "border-amber-400 text-amber-200" : "border-transparent text-neutral-500 hover:text-neutral-300"
      }`}>{label}</button>
  );
}

/* ───────── Cook ───────── */
function CookTab({ state, busy, setBusy, refresh }) {
  const owned = state.owned_recipes_full || [];
  const [selected, setSelected] = useState(owned[0]?.recipe_id || null);
  const [anim, setAnim] = useState("idle");
  const [useKiss, setUseKiss] = useState(false);
  const timer = useRef(null);
  useEffect(() => { if (!selected && owned.length) setSelected(owned[0].recipe_id); }, [owned, selected]);

  if (owned.length === 0) return (
    <div className="text-center py-12 text-neutral-500" data-testid="cook-empty">
      <div className="text-5xl mb-3">📜</div>
      No recipes yet. Visit 🛒 Shop or earn drops from Cave/Boss/Trivia.
    </div>
  );

  const recipe = owned.find(r => r.recipe_id === selected) || owned[0];
  const kissesOwned = state.chefs_kiss_owned || 0;
  const onCook = async () => {
    if (busy) return;
    setBusy(true); setAnim("cooking"); playSizzle();
    try {
      const r = await axios.post(`${API}/kitchen/cook`, {
        recipe_id: recipe.recipe_id,
        use_chefs_kiss: useKiss && kissesOwned > 0,
      });
      setAnim("done"); playDing();
      // 🔬 Burnt cook? Different toast, skip the success-flavored stuff.
      if (r.data.failed) {
        toast.error(
          `🔥 BURNT — ${r.data.icon || "🍳"} ${r.data.item_name} ruined. ` +
          `+${r.data.life_ash_awarded} 💀 Life Ash salvaged from the soot.`,
          { duration: 8000 }
        );
        setUseKiss(false);
        refresh();
        return;
      }
      // Headline toast
      let msg = `✨ ${r.data.icon} ${r.data.item_name} done! Claim from 🍽️ Table (heals ${r.data.heal_hp} HP)`;
      if (r.data.recipe_dropped) msg += ` · 📜 New recipe: ${r.data.recipe_dropped.name}!`;
      toast.success(msg, { duration: 7000 });
      // ✨ PERFECT toast (separate so it gets its own moment)
      if (r.data.perfect) {
        toast.success("✨ PERFECT! Dave's mustache twitches in approval.", { duration: 7000 });
      }
      // 💋 Kiss applied
      if (r.data.chefs_kiss_used) {
        toast.success("💋 Chef's Kiss applied", { duration: 4500 });
      }
      // 🎉 Dupe!
      if (r.data.duplicated && r.data.dupe_bay) {
        const viaLabel = r.data.dupe_bay.via === "perfect+kiss"
          ? "✨💋 PERFECT + Kiss"
          : r.data.dupe_bay.via === "perfect" ? "✨ PERFECT" : "💋 Chef's Kiss";
        toast.success(`🎉 DOUBLE PLATED! ${viaLabel} → 2× ${r.data.item_name} on the Table!`, { duration: 9000 });
        playDing();
      }
      // Reset toggle after the cook
      setUseKiss(false);
      // 🏅 Per-badge toast
      (r.data.badges_awarded || []).forEach(b => {
        toast.success(`🏅 BADGE — ${b.icon} ${b.name}${b.title ? ` · Title: ${b.title}` : ""}`, { duration: 9000 });
      });
      refresh();
    } catch (e) {
      setAnim("idle"); toast.error(e?.response?.data?.detail || "Cook failed.");
    } finally {
      setBusy(false);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setAnim("idle"), 2000);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-4">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Recipe</div>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {owned.map(r => (
            <button key={r.recipe_id} data-testid={`cook-recipe-${r.recipe_id}`}
              onClick={() => setSelected(r.recipe_id)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                selected === r.recipe_id ? "border-amber-400 bg-amber-500/10" : "border-neutral-800 hover:border-neutral-600"
              }`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{r.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{r.name?.replace("Recipe — ", "")}</div>
                  <div className="text-[10px] text-neutral-500">Heals {r.heal_hp} HP · {r.rarity}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={`rounded-xl border-2 bg-gradient-to-br from-black/60 to-[#1a0a05] p-4 transition-all ${
        anim === "cooking" ? "border-amber-300 shadow-[0_0_50px_rgba(245,158,11,0.5)] animate-pulse" :
        anim === "done" ? "border-emerald-400 shadow-[0_0_40px_rgba(52,211,153,0.6)]" :
        "border-amber-500/30"
      }`}>
        {anim === "cooking" && <div className="text-center text-4xl animate-bounce mb-2">🔥🍳🔥</div>}
        {anim === "done" && <div className="text-center mb-2 animate-bounce text-4xl">✨🍽️✨</div>}
        <div className="text-center mb-3">
          <div className="text-6xl">{recipe.icon}</div>
          <div className="font-bold text-amber-200 mt-1">{recipe.name?.replace("Recipe — ", "")}</div>
          <div className="text-xs text-emerald-300 mt-1">Heals {recipe.heal_hp} HP</div>
        </div>
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Ingredients</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(recipe.materials || {}).map(([k, v]) => (
              <span key={k} className="px-2 py-1 rounded bg-black/40 border border-neutral-700 text-xs">{v}× {k}</span>
            ))}
            <span className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/40 text-xs text-amber-200">🪙 {recipe.chits_fee}</span>
          </div>
        </div>
        <label className={`mb-2 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition cursor-pointer ${
            useKiss && kissesOwned > 0
              ? "border-pink-400 bg-pink-500/10"
              : "border-neutral-700 bg-black/30 hover:border-pink-500/40"
          } ${kissesOwned <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid="cook-kiss-toggle">
          <span className="flex items-center gap-2 text-sm">
            <span className="text-xl">💋</span>
            <span className="font-bold text-pink-200">Add Chef's Kiss</span>
            <span className="text-[10px] text-neutral-400">(+50% dupe roll · stacks → 75% w/ ✨)</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="text-[10px] text-neutral-500">{kissesOwned} owned</span>
            <input type="checkbox" disabled={kissesOwned <= 0 || busy}
                   checked={useKiss && kissesOwned > 0}
                   onChange={e => setUseKiss(e.target.checked)}
                   data-testid="cook-kiss-checkbox"
                   className="w-4 h-4 accent-pink-400" />
          </span>
        </label>
        <button data-testid="cook-btn" disabled={busy} onClick={onCook}
          className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 disabled:opacity-50 text-black font-black tracking-wider">
          {anim === "cooking" ? "🔥 COOKING…" : anim === "done" ? "✨ DONE" : "🍳 COOK"}
        </button>
        <div className="text-[10px] text-center text-neutral-500 mt-2">
          100% success · 30% chance Dave hands you a NEW recipe · ✨ 10% PERFECT cook
        </div>
      </div>
    </div>
  );
}

/* ───────── Table ───────── */
function TableTab({ state, tick, busy, setBusy, refresh }) {
  const bay = state.holding_bay || [];
  const onClaim = async (bay_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen/claim`, { bay_id });
      toast.success(`🍽️ Claimed ${r.data.item_id} (heals ${r.data.heal_hp} HP)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally { setBusy(false); }
  };
  if (bay.length === 0) return (
    <div className="text-center py-12 text-neutral-500" data-testid="table-empty">
      <div className="text-5xl mb-3">🍽️</div>
      Dave's table is empty. Cook something at the 🍳 Cook tab.
    </div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="table-list">
      {bay.map(b => {
        const left = Math.max(0, (b.seconds_to_autolist || 0) - tick);
        const urgent = left < 3600;
        return (
          <div key={b.bay_id}
            className={`rounded-xl border p-3 ${urgent ? "border-red-500/60 bg-red-950/20" : "border-amber-500/30 bg-black/40"}`}
            data-testid={`table-item-${b.bay_id}`}>
            <div className="text-center text-5xl mb-2">{b.icon || "🍽️"}</div>
            <div className="text-center font-bold text-amber-200 text-sm">{b.item_name}</div>
            <div className="text-center text-[10px] text-emerald-300">Heals {b.heal_hp} HP</div>
            <div className={`text-center text-xs my-2 ${urgent ? "text-red-300" : "text-amber-300"}`}>⏰ Lists in {fmt(left)}</div>
            <button data-testid={`table-claim-${b.bay_id}`} disabled={busy} onClick={() => onClaim(b.bay_id)}
              className="w-full px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold">
              🍴 Claim
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ───────── Shop ───────── */
function ShopTab({ state, busy, setBusy, refresh }) {
  const [qty, setQty] = useState({});
  const catalog = state.shop_catalog || [];
  const discount = state.daily_discount || {};
  const onBuy = async (item) => {
    if (busy) return;
    const q = Math.max(1, Math.min(50, qty[item.item_id] || 1));
    const isKiss = item.tier === "kiss";
    const unitPrice = isKiss ? item.price_od : item.price_chits;
    const total = unitPrice * q;
    const curIcon = isKiss ? "Ⓞ OD" : "🪙 Chits";
    // 🧾 Roc #38 — confirm before every kitchen buy.
    const ok = await confirmDialog({
      title: `Buy ${q}× ${item.name}?`,
      message: `${item.icon || ""} ${item.name}
Cost: ${total.toLocaleString()} ${curIcon}.
${item.description || ""}`,
      confirmLabel: `Buy — ${total.toLocaleString()} ${curIcon}`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen/buy`, { item_id: item.item_id, qty: q });
      const cur = r.data.currency === "omni_dollars" ? "Ⓞ" : "🪙";
      let msg = `${item.icon} +${r.data.units_added} ${item.name} (-${r.data.spent} ${cur})`;
      if (r.data.discount_applied_pct > 0) msg += ` · 🎲 ${r.data.discount_applied_pct}% off!`;
      if (r.data.eggs_awarded?.length) {
        msg += ` · 🥚 ${r.data.eggs_awarded.join(", ")}!`;
      }
      toast.success(msg, { duration: 6000 });
      (r.data.badges_awarded || []).forEach(b => {
        toast.success(`🏅 BADGE — ${b.icon} ${b.name}${b.title ? ` · Title: ${b.title}` : ""}`, { duration: 9000 });
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy failed.");
    } finally { setBusy(false); }
  };
  const tierGroups = { seed: [], food: [], material: [], butter: [], kiss: [] };
  catalog.forEach(it => { if (tierGroups[it.tier]) tierGroups[it.tier].push(it); });
  const groupLabel = {
    seed: "🌱 Seeds", food: "🍞 Foods",
    material: "🌾 Pantry Materials",
    butter: "🧈 Dave's Butter Gambit",
    kiss: "💋 Chef's Kiss (OD)",
  };
  const fmtSecs = (s) => {
    if (!s || s <= 0) return "—";
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  return (
    <div className="space-y-5" data-testid="shop-list">
      {/* 🎲 Daily Discount Roulette banner */}
      {discount.item_id && (
        <div className="rounded-xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-950/30 via-black/40 to-amber-950/30 p-3 flex items-center gap-3"
             data-testid="shop-discount-banner">
          <span className="text-2xl animate-pulse">🎲</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-fuchsia-200">
              Dave's Daily Discount · <span className="text-amber-200">{discount.discount_pct}% OFF</span> one item
            </div>
            <div className="text-[10px] text-neutral-400 truncate">
              Today's pick: <span className="text-fuchsia-100 font-bold">{discount.item_id}</span> · resets in {fmtSecs(discount.resets_in_seconds)}
            </div>
          </div>
        </div>
      )}
      {["seed", "food", "material", "butter", "kiss"].map(tier => {
        const items = tierGroups[tier];
        if (!items.length) return null;
        return (
          <div key={tier}>
            <div className="text-xs uppercase tracking-wider text-amber-300 font-bold mb-2">{groupLabel[tier]}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {items.map(it => {
                const isKiss = tier === "kiss";
                const price = isKiss ? it.price_od : it.price_chits;
                const origPrice = isKiss ? it.original_price_od : it.original_price_chits;
                const totalPrice = price * (qty[it.item_id] || 1);
                const wallet = isKiss ? (state.omni_dollars || 0) : state.chits;
                const canAfford = wallet >= totalPrice;
                const isDiscounted = it.discount_pct > 0;
                return (
                  <div key={it.item_id} className={`relative rounded-lg border p-2 text-center ${
                      tier === "butter" ? "border-amber-400/50 bg-gradient-to-br from-amber-900/20 to-black/40" :
                      tier === "kiss" ? "border-pink-400/50 bg-gradient-to-br from-pink-900/20 to-black/40" :
                      "border-neutral-700 bg-black/40"
                    } ${isDiscounted ? "ring-2 ring-fuchsia-400/60" : ""}`} data-testid={`shop-item-${it.item_id}`}>
                    {isDiscounted && (
                      <span className="absolute -top-2 -right-2 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-white shadow"
                            data-testid={`shop-discount-${it.item_id}`}>
                        🎲 −{it.discount_pct}%
                      </span>
                    )}
                    <div className="text-3xl">{it.icon}</div>
                    <div className="font-bold text-xs mt-1 truncate">{it.name}</div>
                    <div className="text-[10px] text-neutral-500 leading-tight">{it.description}</div>
                    {!isKiss && tier !== "butter" && (
                      <input type="number" min={1} max={50} value={qty[it.item_id] || 1}
                        data-testid={`shop-qty-${it.item_id}`}
                        onChange={e => setQty(q => ({ ...q, [it.item_id]: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) }))}
                        className="w-full mt-1 px-2 py-1 rounded bg-black/60 border border-neutral-700 text-center text-xs" />
                    )}
                    <button data-testid={`shop-buy-${it.item_id}`} disabled={busy || !canAfford} onClick={() => onBuy(it)}
                      className={`w-full mt-1.5 px-2 py-1 rounded disabled:opacity-50 text-white text-xs font-bold ${
                        isKiss ? "bg-pink-600 hover:bg-pink-500" :
                        tier === "material" ? "bg-emerald-700 hover:bg-emerald-600" :
                        "bg-amber-600 hover:bg-amber-500"
                      }`}>
                      {isDiscounted && origPrice ? (
                        <span><span className="line-through text-white/60 mr-1">{isKiss ? "Ⓞ" : "🪙"} {origPrice * (qty[it.item_id] || 1)}</span>{isKiss ? "Ⓞ" : "🪙"} {totalPrice}</span>
                      ) : (
                        <span>{isKiss ? "Ⓞ" : "🪙"} {totalPrice}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────── Recipes catalog ───────── */
function RecipesTab({ recipes }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recipes-list">
      {recipes.map(r => (
        <div key={r.recipe_id} className={`rounded-xl border p-3 ${
            r.owned ? "border-emerald-500/40 bg-emerald-950/10" : "border-neutral-800 bg-black/30"
          }`} data-testid={`recipe-${r.recipe_id}`}>
          <div className="flex items-start justify-between mb-2">
            <span className="text-4xl">{r.icon}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.owned ? "bg-emerald-500/20 text-emerald-200" : "bg-neutral-700 text-neutral-400"}`}>
              {r.owned ? "OWNED" : "LOCKED"}
            </span>
          </div>
          <div className="font-bold text-sm">{r.name?.replace("Recipe — ", "")}</div>
          <div className="text-[10px] text-neutral-500 uppercase mb-1">{r.rarity} · heals {r.heal_hp} HP</div>
          <div className="text-xs text-neutral-400">
            {Object.entries(r.materials || {}).map(([k, v]) => `${v}× ${k}`).join(" · ")}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────── 🏅 Cooking Badges ───────── */
function BadgesTab({ badges, busy, setBusy, refresh }) {
  if (!badges) return null;
  const onSetTitle = async (title) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/kitchen/badges/set-title`, { title });
      toast.success(title ? `👨‍🍳 Active title: ${title}` : "Title cleared");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Set title failed.");
    } finally { setBusy(false); }
  };
  const stats = badges.stats || {};
  return (
    <div data-testid="badges-tab">
      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatChip label="Total Cooks"    value={stats.cooks_total}     testid="badges-stat-cooks" />
        <StatChip label="Legendary Cooks" value={stats.legendary_cooks} testid="badges-stat-legendary" />
        <StatChip label="Dave's Eggs Used" value={stats.daves_eggs_used} testid="badges-stat-daves" />
        <StatChip label="Butter Eggs Won"  value={stats.butter_eggs_won} testid="badges-stat-butter" />
      </div>

      {/* Active title picker */}
      {badges.earned_titles?.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-500/30 bg-black/40 p-3">
          <div className="text-xs uppercase tracking-wider text-amber-300 font-bold mb-2">
            👨‍🍳 Active Title
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="badges-title-none"
                    onClick={() => onSetTitle("")}
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${
                      !badges.active_title ? "border-amber-400 bg-amber-500/20 text-amber-100" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"
                    }`}>
              None
            </button>
            {badges.earned_titles.map(t => (
              <button key={t} data-testid={`badges-title-${t}`}
                      onClick={() => onSetTitle(t)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition ${
                        badges.active_title === t ? "border-amber-400 bg-amber-500/20 text-amber-100" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"
                      }`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Badge grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="badges-grid">
        {badges.badges.map(b => (
          <div key={b.id} className={`rounded-xl border-2 p-3 text-center ${
              b.owned ? "border-amber-400 bg-amber-500/10 shadow-[0_0_18px_rgba(245,158,11,0.3)]" :
              "border-neutral-800 bg-black/30 opacity-80"
            }`} data-testid={`badge-${b.id}`}>
            <div className={`text-4xl mb-1 ${b.owned ? "" : "grayscale opacity-50"}`}>{b.icon}</div>
            <div className={`font-bold text-sm ${b.owned ? "text-amber-200" : "text-neutral-400"}`}>{b.name}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{b.description}</div>
            {b.owned ? (
              <div className="mt-2 text-[10px] font-bold text-emerald-300">✅ Earned</div>
            ) : (
              <>
                <div className="mt-2 h-1.5 rounded-full bg-neutral-900 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-amber-400 transition-all"
                       style={{ width: `${b.progress_pct}%` }} />
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">{b.progress} / {b.threshold}</div>
              </>
            )}
            {b.title && (
              <div className="text-[10px] text-amber-400 mt-1 italic">"{b.title}"</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatChip({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-black/40 p-2.5 text-center" data-testid={testid}>
      <div className="text-2xl font-black text-amber-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{value || 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 leading-tight">{label}</div>
    </div>
  );
}
